import { useState, useEffect } from "react";
import { useServiceCatalog, useTenantServices, useUpdateTenantServices } from "@/hooks/useTenants";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import type { ServiceCatalog } from "@/types";
import {
  Globe, Monitor, Flag, Calendar, GraduationCap, Users,
  TrendingUp, Megaphone, PenTool, Bot, Layout,
} from "lucide-react";

const iconMap: Record<string, React.ElementType> = {
  globe: Globe,
  layout: Layout,
  monitor: Monitor,
  flag: Flag,
  calendar: Calendar,
  "graduation-cap": GraduationCap,
  users: Users,
  "trending-up": TrendingUp,
  megaphone: Megaphone,
  "pen-tool": PenTool,
  bot: Bot,
};

const categoryColors: Record<string, string> = {
  core: "bg-blue-100 text-blue-800",
  addon: "bg-amber-100 text-amber-800",
  premium: "bg-purple-100 text-purple-800",
};

interface Props {
  tenantId: string;
}

export default function TenantServicesTab({ tenantId }: Props) {
  const { data: catalog, isLoading: catalogLoading } = useServiceCatalog();
  const { data: tenantServices, isLoading: servicesLoading } = useTenantServices(tenantId);
  const updateMutation = useUpdateTenantServices();

  // Track local toggle state: service id → enabled
  const [selections, setSelections] = useState<Record<string, boolean>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (catalog && tenantServices) {
      const map: Record<string, boolean> = {};
      for (const cat of catalog) {
        const ts = tenantServices.find((s) => s.service === cat.id);
        map[cat.id] = ts ? ts.enabled : false;
      }
      setSelections(map);
      setDirty(false);
    }
  }, [catalog, tenantServices]);

  const toggle = (serviceId: string) => {
    setSelections((prev) => ({ ...prev, [serviceId]: !prev[serviceId] }));
    setDirty(true);
  };

  const handleSave = async () => {
    const services = Object.entries(selections).map(([service_id, enabled]) => ({
      service_id,
      enabled,
    }));
    try {
      await updateMutation.mutateAsync({ tenantId, services });
      toast.success("Services updated");
      setDirty(false);
    } catch {
      toast.error("Failed to update services");
    }
  };

  if (catalogLoading || servicesLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  const grouped = {
    core: catalog?.filter((s) => s.category === "core") ?? [],
    addon: catalog?.filter((s) => s.category === "addon") ?? [],
    premium: catalog?.filter((s) => s.category === "premium") ?? [],
  };

  const renderServiceCard = (service: ServiceCatalog) => {
    const Icon = iconMap[service.icon] || Globe;
    const enabled = selections[service.id] ?? false;
    const ts = tenantServices?.find((s) => s.service === service.id);
    const isDeployed = !!ts?.deployed_at;

    return (
      <Card
        key={service.id}
        className={`transition-colors ${enabled ? "border-primary/50 bg-primary/5" : ""}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${enabled ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-500"}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-sm">{service.name}</h4>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${categoryColors[service.category]}`}>
                    {service.category}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{service.description}</p>
                {service.requires.length > 0 && (
                  <p className="text-[10px] text-orange-600 mt-1">
                    Requires: {service.requires.join(", ")}
                  </p>
                )}
                {isDeployed && (
                  <Badge variant="outline" className="mt-1 text-[10px]">Deployed</Badge>
                )}
              </div>
            </div>
            <Switch checked={enabled} onCheckedChange={() => toggle(service.id)} />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {(["core", "addon", "premium"] as const).map((cat) =>
        grouped[cat].length > 0 ? (
          <div key={cat}>
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
              {cat === "core" ? "Core Services" : cat === "addon" ? "Add-ons" : "Premium"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {grouped[cat].map(renderServiceCard)}
            </div>
          </div>
        ) : null
      )}

      <div className="flex justify-end pt-2">
        <Button onClick={handleSave} disabled={!dirty || updateMutation.isPending}>
          {updateMutation.isPending ? "Saving..." : "Save Selection"}
        </Button>
      </div>
    </div>
  );
}
